import { css, styled } from 'canoe';
import React, { useRef } from 'react';

import { useEditor, useOffsetFromViewport } from 'craft/hooks';
import { TOOLTIP_BODY_ID } from 'craft/resolvers/Hotspot/HotspotTooltip';
import { getOffsetAndStylesFromParent } from 'craft/utils/viewport';

import { Spacing } from './SpacingAirHandler';
import { getSpacingStyles } from './utils';

const Wrapper = styled.div<{
  top: number;
  left: number;
  width: number;
  height: number;
}>`
  ${({ top, left, width, height }) => css`
    z-index: 2;
    position: absolute;
    top: ${top}px;
    left: ${left}px;
    width: ${width}px;
    height: ${height}px;
    pointer-events: none;
  `}
`;

const SpacingIndicators = styled.div<{
  hide?: boolean;
}>`
  ${({ hide }) => css`
    opacity: ${hide ? '0' : '1'};
  `}
`;

const SpacingIndicator = styled.div`
  position: absolute;
  opacity: 0.4;
`;

const Margin = styled(SpacingIndicator)`
  ${({ theme }) => css`
    background-color: ${theme.colors.Warning};
  `}
`;

const Padding = styled(SpacingIndicator)`
  ${({ theme }) => css`
    background-color: ${theme.colors.Success};
  `}
`;

interface Props {
  nodeId: string;
  spacing: Spacing;
  dom: HTMLElement;
}

export const SpacingAir = ({ nodeId, spacing, dom }: Props) => {
  const ref = useRef<HTMLDivElement>(null);

  const { query } = useEditor();
  const ancestors = query.node(nodeId).ancestors(true);

  const containingNodeDom =
    ancestors.length > 0 ? query.node(ancestors[0]).get()?.dom : query.node(nodeId).get()?.dom;
  const parent = dom.id === TOOLTIP_BODY_ID ? query.node(nodeId).get().dom : containingNodeDom;

  const viewportOffsets = useOffsetFromViewport(dom, ref);
  const { top, left, scrollTop, scrollLeft, documentOffsetLeft } = viewportOffsets;

  const {
    marginLeft,
    marginTop,
    marginRight,
    marginBottom,
    paddingLeft,
    paddingTop,
    paddingRight,
    paddingBottom,
  } = spacing;

  // you can't have less than 0 top or 0 left. this means that some element
  // somewhere is not present, or is only partly visible
  // so we should just not be rendering the spacing
  if (top - scrollTop < 0 || left + documentOffsetLeft - scrollLeft < 0) {
    return null;
  }

  const width = dom.offsetWidth;
  const height = dom.offsetHeight;

  const offsetsAndStyles = {
    ...getOffsetAndStylesFromParent(dom, parent),
    scrollTop: viewportOffsets.scrollTop,
    scrollLeft: viewportOffsets.scrollLeft,
  };

  const key = (name: string) => `${name}-${nodeId}`;
  return (
    <Wrapper
      id={`spacing-air-wrapper-${nodeId}`}
      key={`${nodeId}-${top + left + scrollTop + scrollLeft}`}
      data-craft-ignore-events
      top={top - scrollTop}
      left={left + documentOffsetLeft - scrollLeft}
      width={width}
      height={height}
    >
      <SpacingIndicators
        id={`spacing-air-wrapper-visibility-${nodeId}`}
        ref={ref}
        hide={!ref.current}
      >
        {/* MARGIN */}
        {parseFloat(marginTop) > 0 && (
          <Margin
            key={key('margin-top')}
            css={getSpacingStyles({
              key: 'marginTop',
              dom,
              offsetsAndStyles,
              spacing,
            })}
          />
        )}
        {(marginRight === 'auto' || parseFloat(marginRight) > 0) && (
          <Margin
            key={key('margin-right')}
            css={getSpacingStyles({
              key: 'marginRight',
              dom,
              offsetsAndStyles,
              spacing,
            })}
          />
        )}
        {parseFloat(marginBottom) > 0 && (
          <Margin
            key={key('margin-bottom')}
            css={getSpacingStyles({
              key: 'marginBottom',
              dom,
              offsetsAndStyles,
              spacing,
            })}
          />
        )}
        {(marginLeft === 'auto' || parseFloat(marginLeft) > 0) && (
          <Margin
            key={key('margin-left')}
            css={getSpacingStyles({
              key: 'marginLeft',
              dom,
              offsetsAndStyles,
              spacing,
            })}
          />
        )}

        {/* PADDING */}
        {parseFloat(paddingTop) > 0 && (
          <Padding
            key={key('padding-top')}
            css={getSpacingStyles({
              key: 'paddingTop',
              dom,
              offsetsAndStyles,
              spacing,
            })}
          />
        )}
        {parseFloat(paddingRight) > 0 && (
          <Padding
            key={key('padding-right')}
            css={getSpacingStyles({
              key: 'paddingRight',
              dom,
              offsetsAndStyles,
              spacing,
            })}
          />
        )}
        {parseFloat(paddingBottom) > 0 && (
          <Padding
            key={key('padding-bottom')}
            css={getSpacingStyles({
              key: 'paddingBottom',
              dom,
              offsetsAndStyles,
              spacing,
            })}
          />
        )}
        {parseFloat(paddingLeft) > 0 && (
          <Padding
            key={key('padding-left')}
            css={getSpacingStyles({
              key: 'paddingLeft',
              dom,
              offsetsAndStyles,
              spacing,
            })}
          />
        )}
      </SpacingIndicators>
    </Wrapper>
  );
};
